import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { NATIVE_916_GEOMETRY, type NativePromptConfiguration } from "@shared/ads-express-native-prompts";

const endpoint="/api/admin/ads-express/native-instructions";
type Settings=NativePromptConfiguration&{defaultProse:string;resolvedInstructions:string};
export function AdsExpressNativeInstructions(){
  const {toast}=useToast();
  const query=useQuery<Settings>({queryKey:[endpoint]});
  const [draft,setDraft]=useState<{revision:number;text:string|null}|null>(null);
  const config=query.data;
  const save=useMutation({
    mutationFn:async(generationProse:string|null)=>{
      const response=await apiRequest("PUT",endpoint,{expectedRevision:draft?.revision??config!.revision,generationProse});
      return response.json() as Promise<Settings>;
    },
    onSuccess:next=>{queryClient.setQueryData([endpoint],next);setDraft(null);toast({title:"Native generation instructions saved"});},
    onError:error=>toast({title:"Could not save instructions",description:String(error),variant:"destructive"}),
  });
  if(query.isLoading)return <p className="text-sm text-muted-foreground">Loading native generation instructions…</p>;
  if(!config)return <div className="text-sm text-destructive">Instructions unavailable. <Button variant="outline" onClick={()=>query.refetch()}>Reload</Button></div>;
  const prose=draft?draft.text:config.generationProse;
  const effective=prose??config.defaultProse;
  return <section className="space-y-3 rounded-lg border p-4">
    <div>
      <h3 className="font-medium">Native 9:16 generation instructions</h3>
      <p className="text-sm text-muted-foreground">Application-added instructions sent with the first native render. Standard Ads Express only; not memes, other ratios, or 4:5-to-9:16 expansion. Sim workflow prompts are unchanged.</p>
    </div>
    <p className="text-xs text-muted-foreground">Revision {config.revision} · {prose===null?"Inherited default":"Custom text"}{config.updatedAt?` · Saved ${new Date(config.updatedAt).toLocaleString()}`:""}</p>
    <Label htmlFor="native-generation-prose">First-pass composition instructions</Label>
    <Textarea id="native-generation-prose" value={effective} rows={9} maxLength={8000}
      disabled={save.isPending} onChange={event=>setDraft({revision:draft?.revision??config.revision,text:event.target.value})}/>
    <details>
      <summary className="cursor-pointer text-sm">Default instruction text</summary>
      <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">{config.defaultProse}</pre>
    </details>
    <div className="flex gap-2">
      <Button disabled={save.isPending||!draft||!effective.trim()} onClick={()=>save.mutate(prose?.trim()||null)}>Save instructions</Button>
      <Button variant="outline" disabled={save.isPending} onClick={()=>save.mutate(null)}>Reset to default</Button>
    </div>
    <details>
      <summary className="cursor-pointer text-sm">Resolved instructions, including fixed safe-zone bounds</summary>
      <pre className="mt-2 whitespace-pre-wrap rounded bg-muted p-3 text-xs">{effective}{"\n\n"}{NATIVE_916_GEOMETRY}</pre>
    </details>
    <p className="text-xs text-muted-foreground">Applies to new runs and explicit fresh regenerations. Existing admitted requests keep their saved instructions. No validation, confirmation, or paid correction step is added.</p>
  </section>;
}
